// ============================================================================
// todo-client-mod -- TodoApiClient
//
// HTTP API 客户端，封装对 /api/todos/* 和 /api/groups/* 的调用。
//
// 支持两种使用方式：
// 1. 通过 IFetchService（由 DI 注入，浏览器/SSR 环境自动适配）
// 2. 直接使用（向后兼容，默认使用原生 fetch）
// ============================================================================
/* -------------------------------------------------------------------------- */
/* 工具函数                                                                    */
/* -------------------------------------------------------------------------- */
/**
 * 获取 auth token（从 localStorage）
 */
function getAuthToken() {
    if (typeof window === 'undefined')
        return null;
    return localStorage.getItem('accessToken');
}
/* -------------------------------------------------------------------------- */
/* TodoApiClient                                                              */
/* -------------------------------------------------------------------------- */
/**
 * Todo HTTP API 客户端
 *
 * 封装对 todo-mod 提供的 REST API 调用。
 * 所有方法返回解析后的 JSON 数据，失败时抛出 Error。
 * 写操作自动附带 auth token。
 */
export class TodoApiClient {
    /**
     * @param deps - 依赖对象（DI 注入）或直接传入 IFetchService 实例（兼容旧接口）
     *
     * 支持两种使用方式：
     * 1. DI 注入：new TodoApiClient({ fetchService: instance })
     * 2. 直接构造：new TodoApiClient() 或 new TodoApiClient(instance)
     */
    constructor(deps) {
        if (deps && typeof deps === 'object' && 'fetchService' in deps) {
            // DI 注入
            this.fetchService = deps.fetchService ?? null;
        }
        else {
            // 兼容旧接口
            this.fetchService = deps ?? null;
        }
    }
    /**
     * 使用 IFetchService 或原生 fetch 发起请求
     */
    async doFetch(url, options) {
        if (this.fetchService) {
            return this.fetchService.fetch(url, options);
        }
        // 向后兼容：直接使用原生 fetch
        const response = await globalThis.fetch(url, {
            method: options?.method ?? 'GET',
            headers: options?.headers,
            body: options?.body,
        });
        return {
            status: response.status,
            ok: response.ok,
            json: () => response.json(),
            text: () => response.text(),
        };
    }
    /**
     * 认证请求头（在有 token 时附带）
     */
    get authHeaders() {
        const headers = {};
        const token = getAuthToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        return headers;
    }
    /* ------------------------------------------------------------------------ */
    /* Todo API                                                                */
    /* ------------------------------------------------------------------------ */
    /**
     * 获取 Todo 列表（分页 + 过滤 + 排序）
     */
    async getTodos(params) {
        const searchParams = new URLSearchParams();
        if (params?.groupId)
            searchParams.set('groupId', params.groupId);
        if (params?.page)
            searchParams.set('page', String(params.page));
        if (params?.pageSize)
            searchParams.set('pageSize', String(params.pageSize));
        if (params?.status)
            searchParams.set('status', params.status);
        if (params?.priority)
            searchParams.set('priority', params.priority);
        if (params?.tags)
            searchParams.set('tags', params.tags);
        if (params?.sortBy)
            searchParams.set('sortBy', params.sortBy);
        if (params?.sortOrder)
            searchParams.set('sortOrder', params.sortOrder);
        const queryStr = searchParams.toString();
        const url = `/api/todos${queryStr ? `?${queryStr}` : ''}`;
        const response = await this.doFetch(url, {
            method: 'GET',
            headers: this.authHeaders,
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to fetch todos: ${response.status}`);
        }
        return data;
    }
    /**
     * 获取单个 Todo 详情
     */
    async getTodo(id) {
        const response = await this.doFetch(`/api/todos/${id}`, {
            method: 'GET',
            headers: this.authHeaders,
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to fetch todo ${id}: ${response.status}`);
        }
        return data;
    }
    /**
     * 创建 Todo（需要认证）
     */
    async createTodo(params) {
        const response = await this.doFetch('/api/todos', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...this.authHeaders },
            body: JSON.stringify(params),
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to create todo: ${response.status}`);
        }
        return data;
    }
    /**
     * 更新 Todo（需要认证）
     */
    async updateTodo(id, params) {
        const response = await this.doFetch(`/api/todos/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...this.authHeaders },
            body: JSON.stringify(params),
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to update todo ${id}: ${response.status}`);
        }
        return data;
    }
    /**
     * 删除 Todo（需要认证）
     */
    async deleteTodo(id) {
        const response = await this.doFetch(`/api/todos/${id}`, {
            method: 'DELETE',
            headers: this.authHeaders,
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to delete todo ${id}: ${response.status}`);
        }
    }
    /**
     * 快捷完成 Todo（需要认证）
     */
    async completeTodo(id) {
        const response = await this.doFetch(`/api/todos/${id}/complete`, {
            method: 'POST',
            headers: this.authHeaders,
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to complete todo ${id}: ${response.status}`);
        }
        return data;
    }
    /**
     * 获取 Todo 状态统计（需要认证）
     */
    async getStats() {
        const response = await this.doFetch('/api/todos/stats', {
            method: 'GET',
            headers: this.authHeaders,
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to fetch stats: ${response.status}`);
        }
        return data;
    }
    /* ------------------------------------------------------------------------ */
    /* Group API                                                               */
    /* ------------------------------------------------------------------------ */
    /**
     * 获取分组列表（需要认证）
     */
    async getGroups() {
        const response = await this.doFetch('/api/groups', {
            method: 'GET',
            headers: this.authHeaders,
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to fetch groups: ${response.status}`);
        }
        return data;
    }
    /**
     * 获取单个分组详情（需要认证）
     */
    async getGroup(id) {
        const response = await this.doFetch(`/api/groups/${id}`, {
            method: 'GET',
            headers: this.authHeaders,
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to fetch group ${id}: ${response.status}`);
        }
        return data;
    }
    /**
     * 创建分组（需要认证）
     */
    async createGroup(params) {
        const response = await this.doFetch('/api/groups', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...this.authHeaders },
            body: JSON.stringify(params),
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to create group: ${response.status}`);
        }
        return data;
    }
    /**
     * 更新分组（需要认证）
     */
    async updateGroup(id, params) {
        const response = await this.doFetch(`/api/groups/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...this.authHeaders },
            body: JSON.stringify(params),
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to update group ${id}: ${response.status}`);
        }
        return data;
    }
    /**
     * 删除分组（Todo 迁移到默认分组）
     */
    async deleteGroup(id) {
        const response = await this.doFetch(`/api/groups/${id}`, {
            method: 'DELETE',
            headers: this.authHeaders,
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to delete group ${id}: ${response.status}`);
        }
    }
    /**
     * 获取全局分组统计（需要认证）
     */
    async getGroupStats() {
        const response = await this.doFetch('/api/groups/stats', {
            method: 'GET',
            headers: this.authHeaders,
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to fetch group stats: ${response.status}`);
        }
        return data;
    }
}
/** 默认单例实例（向后兼容，使用原生 fetch） */
export const todoApiClient = new TodoApiClient();
// ============================================================================
// 容器感知的 TodoApiClient（可选，由 spa-entry 在初始化时注入）
// ============================================================================
let _injectedClient = null;
/**
 * 注入 TodoApiClient 实例（由 spa-entry 在容器加载后调用）
 */
export function setTodoApiClient(client) {
    _injectedClient = client;
}
/**
 * 获取当前可用的 TodoApiClient 实例
 * 优先返回注入的实例，回退到默认单例
 */
export function getTodoApiClient() {
    return _injectedClient ?? todoApiClient;
}
//# sourceMappingURL=todo-api-client.js.map